import {
  type DropTargetDropEvent,
  IconButton,
  MenuItem,
  useDropTarget,
} from '@affine/component';
import { DocsService } from '@affine/core/modules/doc';
import { NavigationPanelService } from '@affine/core/modules/navigation-panel';
import { OrganizeService } from '@affine/core/modules/organize';
import { TemplateDocService } from '@affine/core/modules/template-doc';
import { WorkbenchService } from '@affine/core/modules/workbench';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { inferOpenMode } from '@affine/core/utils';
import { useI18n } from '@affine/i18n';
import { AddOrganizeIcon, PlusIcon, TemplateIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService, useServices } from '@toeverything/infra';
import {
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { CollapsibleSection } from '../../layouts/collapsible-section';
import { NavigationPanelDocNode } from '../../nodes/doc';
import { NavigationPanelFolderNode } from '../../nodes/folder';
import { DropEffect, NavigationPanelTreeRoot } from '../../tree';
import type { NodeOperation } from '../../tree/types';
import { templatesRootCanDrop, templatesRootDropEffect } from './dnd';
import { RootEmpty } from './empty';

const childLocation = {
  at: 'navigation-panel:templates:root' as const,
};

/**
 * Templates behave exactly like a project in the sidebar: template folders are
 * rendered as a real folder tree (nesting, opening and drag & drop included),
 * followed by the loose docs flagged as template.
 */
export const NavigationPanelTemplates = () => {
  const {
    organizeService,
    templateDocService,
    navigationPanelService,
    docsService,
    workbenchService,
  } = useServices({
    OrganizeService,
    TemplateDocService,
    NavigationPanelService,
    DocsService,
    WorkbenchService,
  });
  const t = useI18n();
  const path = useMemo(() => ['templates'], []);
  const collapsed = useLiveData(navigationPanelService.collapsed$(path));
  const [newFolderId, setNewFolderId] = useState<string | null>(null);

  const folderTree = organizeService.folderTree;
  const rootFolder = folderTree.rootFolder;
  const isLoading = useLiveData(folderTree.isLoading$);

  const templateFolderIds = useLiveData(
    templateDocService.folderList.templateFolderIds$
  );
  const templateDocIds = useLiveData(
    templateDocService.list.liveTemplateDocIds$
  );

  // template folders, sorted by name; ids of deleted folders are dropped
  const folderIds = useMemo(() => {
    return templateFolderIds
      .map(id => ({
        id,
        name: folderTree.folderNode$(id).value?.name$.value ?? null,
      }))
      .filter(entry => entry.name !== null)
      .sort((a, b) => (a.name as string).localeCompare(b.name as string))
      .map(entry => entry.id);
  }, [folderTree, templateFolderIds]);

  // a doc living inside a template folder is already shown by that folder
  const standaloneDocIds = useMemo(() => {
    const inFolders = new Set(
      templateFolderIds.flatMap(folderId =>
        organizeService.collectDocIds(folderId)
      )
    );
    return templateDocIds.filter(id => !inFolders.has(id));
  }, [organizeService, templateDocIds, templateFolderIds]);

  const handleCreateFolder = useCallback(() => {
    const id = rootFolder.createFolder(
      t['com.affine.rootAppSidebar.templates.new-template-folder'](),
      rootFolder.indexAt('before')
    );
    templateDocService.folderList.setTemplate(id, true);
    navigationPanelService.setCollapsed(path, false);
    setNewFolderId(id);
    return id;
  }, [navigationPanelService, path, rootFolder, t, templateDocService]);

  const handleCreateDoc = useCallback(
    (e: MouseEvent) => {
      const record = docsService.createDoc({ isTemplate: true });
      navigationPanelService.setCollapsed(path, false);
      workbenchService.workbench.openDoc(record.id, {
        at: inferOpenMode(e),
      });
    },
    [docsService, navigationPanelService, path, workbenchService]
  );

  const handleDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      const entity = data.source.data.entity;
      if (!entity) {
        return;
      }
      if (entity.type === 'folder') {
        templateDocService.folderList.setTemplate(entity.id, true);
      } else if (entity.type === 'doc') {
        templateDocService.list.setTemplate(entity.id, true);
      } else {
        return;
      }
      navigationPanelService.setCollapsed(path, false);
    },
    [navigationPanelService, path, templateDocService]
  );

  const { dropTargetRef, draggedOverDraggable, draggedOverPosition } =
    useDropTarget<AffineDNDData>(
      () => ({
        data: { at: 'navigation-panel:templates:root' },
        onDrop: handleDrop,
        canDrop: templatesRootCanDrop,
      }),
      [handleDrop]
    );

  useEffect(() => {
    if (collapsed) setNewFolderId(null); // reset to clear the renaming state
  }, [collapsed]);

  return (
    <CollapsibleSection
      path={path}
      title={t['com.affine.rootAppSidebar.templates']()}
      headerRef={dropTargetRef}
      testId="navigation-panel-templates"
      headerTestId="navigation-panel-templates-category-divider"
      actions={
        <>
          <IconButton
            data-testid="navigation-panel-bar-add-template-doc-button"
            onClick={handleCreateDoc}
            onAuxClick={handleCreateDoc}
            size="16"
            tooltip={t['com.affine.template-list.create-new']()}
          >
            <PlusIcon />
          </IconButton>
          <IconButton
            data-testid="navigation-panel-bar-add-template-folder-button"
            onClick={handleCreateFolder}
            size="16"
            tooltip={t[
              'com.affine.rootAppSidebar.templates.new-template-folder'
            ]()}
          >
            <AddOrganizeIcon />
          </IconButton>
          {draggedOverDraggable && (
            <DropEffect
              position={draggedOverPosition}
              dropEffect={templatesRootDropEffect({
                source: draggedOverDraggable,
                treeInstruction: null,
              })}
            />
          )}
        </>
      }
    >
      <NavigationPanelTreeRoot
        placeholder={
          <RootEmpty
            isLoading={isLoading}
            onClickCreate={handleCreateFolder}
            onDrop={handleDrop}
          />
        }
      >
        {folderIds.map(id => (
          <NavigationPanelFolderNode
            key={id}
            nodeId={id}
            defaultRenaming={id === newFolderId}
            location={childLocation}
            parentPath={path}
          />
        ))}
        {standaloneDocIds.map(id => (
          <TemplateDocNode key={id} docId={id} parentPath={path} />
        ))}
      </NavigationPanelTreeRoot>
    </CollapsibleSection>
  );
};

const TemplateDocNode = ({
  docId,
  parentPath,
}: {
  docId: string;
  parentPath: string[];
}) => {
  const t = useI18n();
  const templateDocService = useService(TemplateDocService);

  const handleRemove = useCallback(() => {
    templateDocService.list.setTemplate(docId, false);
  }, [docId, templateDocService]);

  const operations = useMemo<NodeOperation[]>(
    () => [
      {
        index: 99,
        view: (
          <MenuItem prefixIcon={<TemplateIcon />} onClick={handleRemove}>
            {t['com.affine.template-list.remove-from-templates']()}
          </MenuItem>
        ),
      },
    ],
    [handleRemove, t]
  );

  return (
    <NavigationPanelDocNode
      docId={docId}
      location={childLocation}
      operations={operations}
      parentPath={parentPath}
    />
  );
};
